import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RootState, AppDispatch } from '../index'
import { API_BASE_URL } from '../../config/api'
import { setCredentials, logout } from '../slices/authSlice'
import { mockBaseQuery } from '../../mock/mockBaseQuery'

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true'

// Ensure baseUrl doesn't have trailing slash
const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL

const realBaseQuery = fetchBaseQuery({
  baseUrl: baseUrl,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState
    const token = state.auth?.token
    if (import.meta.env.DEV && !USE_MOCK) {
      console.log('[API] Token status:', token ? 'Present' : 'Missing', token ? `(${token.substring(0, 20)}...)` : '')
    }
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    } else {
      const localToken = localStorage.getItem('token')
      if (localToken) headers.set('authorization', `Bearer ${localToken}`)
    }
    return headers
  },
})

// Base query with automatic token refresh on 401 (chỉ dùng khi không dùng mock)
const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  let result = await realBaseQuery(args, api, extraOptions)
  
  // If we get a 401 and it's not the refresh endpoint itself, try to refresh the token
  if (result.error && result.error.status === 401 && !args.url?.includes('/auth/refresh')) {
    const state = api.getState() as RootState
    const token = state.auth?.token || localStorage.getItem('token')
    
    if (token) {
      // Try to refresh token (use the old token for refresh)
      try {
        const refreshResult = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        })
        
        if (refreshResult.ok) {
          const refreshData = await refreshResult.json()
          const { access_token } = refreshData
          
          // Update token in store
          const dispatch = api.dispatch as AppDispatch
          dispatch(setCredentials({ token: access_token, user: state.auth.user }))
          
          // Retry original request with new token
          result = await realBaseQuery(args, api, extraOptions)
        } else {
          // Refresh failed, logout user
          const dispatch = api.dispatch as AppDispatch
          dispatch(logout())
          // Redirect to login if not already there
          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
          }
        }
      } catch (error) {
        console.error('[API] Token refresh failed:', error)
        // Refresh failed, logout user
        const dispatch = api.dispatch as AppDispatch
        dispatch(logout())
        // Redirect to login if not already there
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
      }
    } else {
      // No token, logout user
      const dispatch = api.dispatch as AppDispatch
      dispatch(logout())
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
  }
  
  return result
}

// POC: dùng mock; triển khai thật: set VITE_USE_MOCK=false trong .env
const baseQuery = USE_MOCK
  ? mockBaseQuery
  : baseQueryWithReauth

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery,
  tagTypes: ['User', 'Cases', 'Case', 'Filings', 'Documents', 'CourtSessions', 'Alerts', 'Judges'],
  endpoints: () => ({}),
})
